import {
  EDUCATION_CATEGORIES,
  EDUCATION_ITEMS_BY_CATEGORY,
  EducationListItemType,
} from '@/modules/education/Education.constants';
import { PresentationBundleKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

/**
 * What happens when a leaf or top-level section in the slide picker is clicked.
 *
 * - `bundle`: add a new bundle of the given kind (going through the
 *   configuration modal if the bundle requires it). When `generate` is true,
 *   the persist layer flags the new bundle for backend AI generation.
 * - `education-slide`: add a single education bundle for the given slide id,
 *   skipping the bulk-add modal.
 * - `open-education-modal`: open the existing bulk-add education modal.
 * - `template-custom-page`: add a custom page bundle that copies a custom page
 *   from a presentation template.
 */
export type CatalogAction =
  | { kind: 'bundle'; bundleKind: PresentationBundleKind; generate?: boolean }
  | { kind: 'education-slide'; slideId: EducationListItemType }
  | { kind: 'open-education-modal' }
  | {
      kind: 'template-custom-page';
      templatePageId: string;
      customPage: TemplateCustomPageContent;
    };

/** The fields that the menu copies from a template custom page. */
export interface TemplateCustomPageContent {
  pageName: string;
  pageContent: string;
  pageHeader: string;
  pageSubheader?: string | null;
  includePageHeader?: boolean | null;
  customPrompt?: string | null;
}

export enum CatalogCategoryType {
  CATALOG_SECTION_GENERAL = 'CATALOG_SECTION_GENERAL',
  CATALOG_SECTION_ESTATE_DETAILS = 'CATALOG_SECTION_ESTATE_DETAILS',
  CATALOG_SECTION_AI_GENERATED = 'CATALOG_SECTION_AI_GENERATED',
  CATALOG_SECTION_EDUCATIONAL = 'CATALOG_SECTION_EDUCATIONAL',
  /**
   * Custom pages from the user's and the firm's presentation templates. This
   * section is built at runtime from a query, so it is not part of
   * `SLIDE_PICKER_CATALOG`.
   */
  CATALOG_SECTION_TEMPLATE_CUSTOM_PAGES = 'CATALOG_SECTION_TEMPLATE_CUSTOM_PAGES',
}

export interface CatalogLeaf {
  /**
   * For bundle leaves, this is the literal label. For education slides we
   * leave this undefined and look up the (potentially tenant-overridden) title
   * via `useEducationMap()` at render time.
   */
  label?: string;
  action: CatalogAction;
}

export interface CatalogGroup {
  label: string;
  leaves: CatalogLeaf[];
}

/**
 * A top-level section in the slide picker. Sections either have direct
 * leaves (flat) or nested groups that each render as an inline section-header
 * label inside the flyout.
 */
export interface CatalogSection {
  label: string;
  catalogCategoryType: CatalogCategoryType;
  /** Direct leaves rendered above any nested groups. */
  leaves?: CatalogLeaf[];
  /** Nested flyout groups, each rendered with an inline section-header label. */
  groups?: CatalogGroup[];
  /** Info tooltip body shown next to the section label. */
  tooltipText?: string;
}

const generalLeaves: CatalogLeaf[] = [
  {
    label: 'Cover page',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.CoverSlideBundle,
    },
  },
  {
    label: 'Custom page',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.CustomPageBundle,
    },
  },
  {
    label: 'Divider page',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.DividerBundle,
    },
  },
  {
    label: 'Table of contents',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.TableOfContentsBundle,
    },
  },
  {
    label: 'Firm disclaimer',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.StandaloneDisclaimerBundle,
    },
  },
  {
    label: 'Luminary disclaimer',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.LuminaryDisclaimerBundle,
    },
  },
];

const estateDetailsLeaves: CatalogLeaf[] = [
  {
    label: 'Balance sheet',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.BalanceSheetBundle,
    },
  },
  {
    label: 'Entities list',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.EntitiesOverviewBundle,
    },
  },
  {
    label: 'Insights',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.EstateInsightsBundle,
    },
  },
  {
    label: 'Liabilities',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.LiabilitiesBundle,
    },
  },
  {
    label: 'Life insurance',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.LifeInsuranceBundle,
    },
  },
  {
    label: 'Professional team',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.ProfessionalTeamBundle,
    },
  },
];

const visualizationsLeaves: CatalogLeaf[] = [
  {
    label: 'Beneficiary report',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.BeneficiaryReportBundle,
    },
  },
  {
    label: 'Entity overview',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.EntitySummaryBundle,
    },
  },
  {
    label: 'Family tree',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.FamilyTreeBundle,
    },
  },
  {
    label: 'Ownership diagram',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.EntityGraphViewBundle,
    },
  },
  {
    label: 'Waterfall overview',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.WaterfallOverviewBundle,
    },
  },
  {
    label: 'Waterfall comparison',
    action: {
      kind: 'bundle',
      bundleKind: PresentationBundleKind.WaterfallComparisonBundle,
    },
  },
];

function educationLeaves(ids: readonly EducationListItemType[]): CatalogLeaf[] {
  return ids.map((slideId) => ({
    action: { kind: 'education-slide', slideId },
  }));
}

const SKIP_AI_GENERATE_BUNDLES: ReadonlySet<PresentationBundleKind> = new Set([
  PresentationBundleKind.DocumentSummaryBundle,
]);

// AI-generated section. Document summaries use an existing in-app summary.
// Other leaves use a backend generation workflow — see `generate` below.
// The persist layer reads that flag and sends a
// matching `generate: true` on the create input, which spawns a
// `PRESENTATION_SLIDE_GENERATE` workflow tracker. The designer shell watches
// for that tracker, blocks the UI while it runs, and refetches on
// completion.
const aiBackedLeaves: CatalogLeaf[] = [
  {
    label: 'Document summary',
    bundleKind: PresentationBundleKind.DocumentSummaryBundle,
  },
  {
    label: 'Asset distribution detail',
    bundleKind: PresentationBundleKind.AssetDistributionBundle,
  },
  {
    label: 'Asset distribution diagram',
    bundleKind: PresentationBundleKind.DiagramBundle,
  },
  {
    label: 'Businesses overview',
    bundleKind: PresentationBundleKind.BusinessOverviewBundle,
  },
  {
    label: 'Documents reviewed',
    bundleKind: PresentationBundleKind.DocumentsReviewedBundle,
  },
  {
    label: 'Family overview',
    bundleKind: PresentationBundleKind.FamilyOverviewBundle,
  },
  {
    label: 'Healthcare directives',
    bundleKind: PresentationBundleKind.HealthcareDirectiveBundle,
  },
  {
    label: 'Observations & opportunities',
    bundleKind: PresentationBundleKind.PlanningOpportunitiesBundle,
  },
  {
    label: 'Powers of attorney',
    bundleKind: PresentationBundleKind.PowerOfAttorneyBundle,
  },
  {
    label: 'Trusts overview',
    bundleKind: PresentationBundleKind.TrustOverviewBundle,
  },
  { label: 'Wills', bundleKind: PresentationBundleKind.WillBundle },
].map<CatalogLeaf>(({ label, bundleKind }) => ({
  label,
  action: {
    kind: 'bundle',
    bundleKind,
    generate: !SKIP_AI_GENERATE_BUNDLES.has(bundleKind),
  },
}));

const aiGeneratedBundleKinds = new Set(
  aiBackedLeaves.flatMap(({ action }) =>
    action.kind === 'bundle' ? [action.bundleKind] : []
  )
);

export function isAIGeneratedBundleKind(kind: PresentationBundleKind) {
  return aiGeneratedBundleKinds.has(kind);
}

export const SLIDE_PICKER_CATALOG: CatalogSection[] = [
  {
    label: 'General',
    catalogCategoryType: CatalogCategoryType.CATALOG_SECTION_GENERAL,
    leaves: generalLeaves,
  },
  {
    label: 'Household details',
    catalogCategoryType: CatalogCategoryType.CATALOG_SECTION_ESTATE_DETAILS,
    tooltipText:
      "Content in this section is linked to the client's underlying data.",
    groups: [
      { label: 'Estate details', leaves: estateDetailsLeaves },
      { label: 'Visualizations', leaves: visualizationsLeaves },
    ],
  },
  {
    label: 'AI-generated',
    catalogCategoryType: CatalogCategoryType.CATALOG_SECTION_AI_GENERATED,
    tooltipText:
      "Content in this section is generated by AI using the client's documents and is not linked to the client's underlying data. Updates to these slides will not impact any client details. Edits to document summary slides will update the in-app document summary.",
    leaves: aiBackedLeaves,
  },
  {
    label: 'Education',
    catalogCategoryType: CatalogCategoryType.CATALOG_SECTION_EDUCATIONAL,
    // An ungrouped entry at the top of the flyout opens the bulk-add modal;
    // the grouped leaves below add a single education slide each.
    leaves: [
      {
        label: 'Add multiple pages...',
        action: { kind: 'open-education-modal' },
      },
    ],
    groups: EDUCATION_CATEGORIES.map((category) => ({
      label: category,
      leaves: educationLeaves(EDUCATION_ITEMS_BY_CATEGORY[category]),
    })),
  },
];

export interface GetSlideCatalogOptions {
  includeCategories?: Set<CatalogCategoryType>;
  excludeCategories?: Set<CatalogCategoryType>;
}

export function getSlideCatalog(
  options?: GetSlideCatalogOptions
): CatalogSection[] {
  const includeCategories = options?.includeCategories;
  const excludeCategories = options?.excludeCategories;
  const overlappingCategories = includeCategories
    ? [...includeCategories].filter((category) =>
        excludeCategories?.has(category)
      )
    : [];

  if (overlappingCategories.length > 0) {
    diagnostics.warn(
      `Slide catalog categories cannot be both included and excluded. Excluding: ${overlappingCategories.join(
        ', '
      )}`
    );
  }

  return SLIDE_PICKER_CATALOG.filter(({ catalogCategoryType }) => {
    if (excludeCategories?.has(catalogCategoryType)) return false;
    return includeCategories?.has(catalogCategoryType) ?? true;
  });
}

export function getCatalogEntries(
  options?: GetSlideCatalogOptions
): Set<CatalogLeaf> {
  return new Set(
    getSlideCatalog(options).flatMap((section) => [
      ...(section.leaves ?? []),
      ...(section.groups?.flatMap((group) => group.leaves) ?? []),
    ])
  );
}
